/**
 * AdminWebhooksPage - /admin/webhooks. Phase 9.11.
 *
 * Two sections:
 *  - Webhook endpoints: list + create + toggle active + delete.
 *  - Recent deliveries: list (filterable by endpoint + status) + replay.
 *
 * The newly created endpoint's HMAC signing secret is shown exactly
 * once via RevealOnceModal. The endpoint list is cached in
 * `endpointsById` so the deliveries table can render endpoint names.
 */
import { useMemo, useState } from "react";

import { Alert, Button, Card, Select, Spinner } from "@/components/ui";
import { usePageTitle } from "@/hooks/usePageTitle";
import {
  useCreateWebhookEndpoint,
  useDeleteWebhookEndpoint,
  useUpdateWebhookEndpoint,
  useWebhookEndpoints,
} from "@/hooks/useWebhookEndpoints";
import {
  useReplayDelivery,
  useWebhookDeliveries,
} from "@/hooks/useWebhookDeliveries";
import { CreateWebhookEndpointModal } from "@/components/admin/CreateWebhookEndpointModal";
import { RevealOnceModal } from "@/components/admin/RevealOnceModal";
import { WebhookDeliveriesTable } from "@/components/admin/WebhookDeliveriesTable";
import { WebhookEndpointTable } from "@/components/admin/WebhookEndpointTable";
import type {
  WebhookDeliveryStatus,
  WebhookEndpointCreateRequest,
  WebhookEndpointResponse,
} from "@/types/api";

const STATUS_FILTER_OPTIONS: {
  value: "" | WebhookDeliveryStatus;
  label: string;
}[] = [
  { value: "", label: "All statuses" },
  { value: "pending", label: "Pending" },
  { value: "success", label: "Success" },
  { value: "failed_retry", label: "Retrying" },
  { value: "failed_dead", label: "Dead" },
];

function extractErrorMessage(err: unknown, fallback: string): string {
  const detail = (err as {
    response?: { data?: { detail?: string } };
  })?.response?.data?.detail;
  return typeof detail === "string" ? detail : fallback;
}

export function AdminWebhooksPage(): JSX.Element {
  usePageTitle("Webhooks");

  // -- Endpoints ----------------------------------------------------------
  const endpointsQuery = useWebhookEndpoints();
  const createEndpoint = useCreateWebhookEndpoint();
  const updateEndpoint = useUpdateWebhookEndpoint();
  const deleteEndpoint = useDeleteWebhookEndpoint();

  const endpoints = useMemo(
    () => endpointsQuery.data ?? [],
    [endpointsQuery.data],
  );
  const endpointsById = useMemo(() => {
    const map: Record<string, WebhookEndpointResponse> = {};
    for (const ep of endpoints) {
      map[ep.id] = ep;
    }
    return map;
  }, [endpoints]);

  const [createOpen, setCreateOpen] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [revealedSecret, setRevealedSecret] = useState<{
    endpointName: string;
    secret: string;
  } | null>(null);

  const handleCreate = async (body: WebhookEndpointCreateRequest) => {
    setCreateError(null);
    try {
      const created = await createEndpoint.mutateAsync(body);
      setRevealedSecret({
        endpointName: created.name,
        secret: created.secret,
      });
      setCreateOpen(false);
    } catch (err) {
      setCreateError(extractErrorMessage(err, "Could not create endpoint."));
      throw err;
    }
  };

  const handleToggleActive = (
    endpoint: WebhookEndpointResponse,
    nextValue: boolean,
  ) => {
    updateEndpoint.mutate({
      endpointId: endpoint.id,
      body: { is_active: nextValue },
    });
  };

  const handleDelete = (endpointId: string) => {
    deleteEndpoint.mutate(endpointId);
  };

  // -- Deliveries ---------------------------------------------------------
  const [filterEndpointId, setFilterEndpointId] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<
    "" | WebhookDeliveryStatus
  >("");

  const deliveriesQuery = useWebhookDeliveries({
    endpointId: filterEndpointId || null,
    status: filterStatus || null,
  });
  const replay = useReplayDelivery();
  const [replayingId, setReplayingId] = useState<number | null>(null);

  const handleReplay = async (deliveryId: number) => {
    setReplayingId(deliveryId);
    try {
      await replay.mutateAsync(deliveryId);
    } finally {
      setReplayingId(null);
    }
  };

  const endpointFilterOptions = useMemo(
    () => [
      { value: "", label: "All endpoints" },
      ...endpoints.map((ep) => ({ value: ep.id, label: ep.name })),
    ],
    [endpoints],
  );

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-neutral-900">Webhooks</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Manage outbound webhook endpoints and inspect recent deliveries.
        </p>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* Endpoints section                                              */}
      {/* ------------------------------------------------------------- */}
      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-neutral-900">
            Webhook endpoints
          </h2>
          <Button type="button" onClick={() => setCreateOpen(true)}>
            <i
              className="fa-duotone fa-thin fa-plus"
              aria-hidden="true"
            />
            Add endpoint
          </Button>
        </div>

        {endpointsQuery.isError && (
          <Alert variant="error" title="Could not load endpoints">
            Try refreshing the page. If the problem persists, check the
            backend logs.
          </Alert>
        )}

        {endpointsQuery.isLoading ? (
          <Card padding="lg">
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          </Card>
        ) : (
          <WebhookEndpointTable
            endpoints={endpoints}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
            isMutating={
              updateEndpoint.isPending || deleteEndpoint.isPending
            }
          />
        )}
      </section>

      {/* ------------------------------------------------------------- */}
      {/* Deliveries section                                             */}
      {/* ------------------------------------------------------------- */}
      <section className="space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-neutral-900">
            Recent deliveries
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            <Select
              id="filter-endpoint"
              value={filterEndpointId}
              onChange={(e) => setFilterEndpointId(e.target.value)}
              options={endpointFilterOptions}
              className="min-w-44"
              aria-label="Filter deliveries by endpoint"
            />
            <Select
              id="filter-status"
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(
                  e.target.value as "" | WebhookDeliveryStatus,
                )
              }
              options={STATUS_FILTER_OPTIONS}
              className="min-w-40"
              aria-label="Filter deliveries by status"
            />
          </div>
        </div>

        {deliveriesQuery.isError && (
          <Alert variant="error" title="Could not load deliveries">
            Try refreshing the page or selecting a different filter.
          </Alert>
        )}

        {deliveriesQuery.isLoading ? (
          <Card padding="lg">
            <div className="flex items-center justify-center py-8">
              <Spinner size="lg" />
            </div>
          </Card>
        ) : (
          <WebhookDeliveriesTable
            deliveries={deliveriesQuery.data ?? []}
            endpointsById={endpointsById}
            onReplay={(id) => {
              void handleReplay(id);
            }}
            isReplayingId={replayingId}
          />
        )}
      </section>

      <CreateWebhookEndpointModal
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setCreateError(null);
        }}
        onSubmit={handleCreate}
        isSubmitting={createEndpoint.isPending}
        error={createError}
      />

      {revealedSecret && (
        <RevealOnceModal
          isOpen
          onClose={() => setRevealedSecret(null)}
          title={`Signing secret for ${revealedSecret.endpointName}`}
          description="Use this value as the HMAC signing secret. Verify the X-Buyist-Support-Signature header on every incoming delivery."
          secret={revealedSecret.secret}
        />
      )}
    </div>
  );
}

export default AdminWebhooksPage;
